import { Plus } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { ContractsKpiStrip } from "@/features/contracts/components/contracts-kpi-strip"
import {
  ContractsSearchFilters,
  type ContractsFiltersValue,
} from "@/features/contracts/components/contracts-search-filters"
import { ContractsTable } from "@/features/contracts/components/contracts-table"
import { useContracts } from "@/features/contracts/hooks/use-contracts"
import { paths } from "@/routes/paths"

export function ContractsPage() {
  const { t } = useTranslation("contracts")
  const { data, isLoading } = useContracts()
  const [filters, setFilters] = useState<ContractsFiltersValue>({
    search: "",
    status: "all",
    product: "all",
  })

  const filtered = useMemo(() => {
    const lower = filters.search.toLowerCase().trim()
    return (data ?? []).filter((c) => {
      const matchSearch =
        !lower ||
        c.contractNumber?.toLowerCase().includes(lower) ||
        c.clientName?.toLowerCase().includes(lower)
      const matchStatus = filters.status === "all" || c.status === filters.status
      const matchProduct =
        filters.product === "all" || c.productType === filters.product
      return matchSearch && matchStatus && matchProduct
    })
  }, [data, filters])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
        meta={
          data ? (
            <span className="text-muted-foreground text-sm">
              · {t("search.resultsCount", { count: filtered.length })}
            </span>
          ) : null
        }
        actions={
          <Button asChild>
            <Link to={paths.contractNew}>
              <Plus className="size-4" />
              {t("newContract")}
            </Link>
          </Button>
        }
      />

      <ContractsKpiStrip contracts={data ?? []} />

      <ContractsSearchFilters value={filters} onChange={setFilters} />

      {isLoading ? <LoadingState /> : <ContractsTable contracts={filtered} />}
    </div>
  )
}
